import React from "react";
import PropTypes from "prop-types";

// Components
import Modal from "react-bootstrap/Modal";

/**
 * @typedef {Object} headerObject
 * @property {string} id - used for the header title <Modal.Title>.
 * @property {string} class - classes used for the header title <Modal.Title>.
 * @property {string} sectionClass - classes used for the header <Modal.Header>.
 * @property {string} text - header text title.
 */

/**
 * @typedef {Object} contentObject
 * @property {string} class - classes used for the modal body container <Modal.Body>.
 * @property {ReactNode[]} content - array with individual react nodes.
 */

/**
 * This component renders a generic modal with header, content and footer with custom content.
 *
 * @param {Object} props - The object containing props.
 * @param {function} props.handleClose - close handler callback.
 * @param {boolean} props.show - wether the modal is open or closed.
 * @param {string} props.modalSize - the defined size with for the modal content container ["sm", "lg", "xl"].
 * @param {headerObject} props.header - header component object data.
 *   - id - used for the header title <Modal.Title>.
 *   - class - classes used for the header title <Modal.Title>.
 *   - sectionClass - classes used for the header <Modal.Header>.
 *   - text - header text title.
 * @param {contentObject} props.body - if true shows the numbers of each list item inside a green circle.
 *   - class - classes used for the modal body container <Modal.Body>.
 *   - content - array with individual react nodes.
 * @param {contentObject} props.footer - string used as id to group the list items with the same id category.
 *   - class - classes used for the modal footer container <Modal.Footer>.
 *   - content - array with individual react nodes.
 * @returns {React.JSX.Element} Returns the JSX component to render.
 */
const ModalGeneric = ({
  handleClose = () => {},
  show = false,
  modalSize = "lg", // sm, lg, xl
  header = {
    id: "",
    class: "",
    sectionClass: "",
    text: ""
  },
  body = {
    class: "",
    content: []
  },
  footer = {
    class: "",
    content: []
  }
}) => {
  return (
    <Modal show={show} onHide={handleClose} size={modalSize}>
      {header && (
        <Modal.Header className={`${header.sectionClass || ""}`} closeButton>
          <Modal.Title id={header.id} className={`${header.class}`}>
            {header.text}
          </Modal.Title>
        </Modal.Header>
      )}

      {body && <Modal.Body className={`${body.class}`}>{body.content.map((component) => component)}</Modal.Body>}

      {footer && (
        <Modal.Footer className={`${footer.class}`}>{footer.content.map((component) => component)}</Modal.Footer>
      )}
    </Modal>
  );
};

export default ModalGeneric;

ModalGeneric.propTypes = {
  handleClose: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  modalSize: PropTypes.oneOf(["sm", "lg", "xl"]),
  header: PropTypes.shape({
    id: PropTypes.string.isRequired,
    class: PropTypes.string,
    sectionClass: PropTypes.string,
    text: PropTypes.string.isRequired
  }),
  body: PropTypes.shape({
    class: PropTypes.string,
    content: PropTypes.arrayOf(PropTypes.node)
  }),
  footer: PropTypes.shape({
    class: PropTypes.string,
    content: PropTypes.arrayOf(PropTypes.node)
  })
};
